import type { ChangeEvent, FC } from 'react';

import type { TToggle } from '@/lib/interfaces/elements/toggle.types';
import cn from '@/lib/packages/cn';
import { toggleInputVariants, toggleThumbVariants, toggleVariants } from '@/lib/packages/toggle.variants';

const Toggle: FC<TToggle> = ({
  checked,
  defaultChecked,
  onChange,
  name,
  id,
  'aria-label': ariaLabel,
  disabled = false,
  required = false,
  size = 'md',
  variant = 'primary',
  className,
  thumbClassName,
  inputClassName,
  icon,
  checkedIcon,
  uncheckedIcon,
  ...props
}) => {
  const handleClick = () => {
    if (disabled) return;

    // Create a synthetic event for compatibility
    const syntheticEvent = {
      target: { checked: !checked },
      currentTarget: { checked: !checked },
    } as ChangeEvent<HTMLInputElement>;

    onChange?.(!checked, syntheticEvent);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleClick();
    }
  };

  const _inputClasses = cn(toggleInputVariants({ disabled }), inputClassName);
  const containerClasses = cn(toggleVariants({ size, variant, disabled }), className);
  const thumbClasses = cn(
    toggleThumbVariants({ size, disabled }),
    checked && 'translate-x-5', // Apply checked transform
    thumbClassName,
  );

  const uncheckedIconToShow = uncheckedIcon || icon;
  const checkedIconToShow = checkedIcon || icon;

  return (
    <div
      className={containerClasses}
      role='switch'
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-checked={checked}
      {...props}
    >
      <span className={cn('relative', thumbClasses)}>
        {(uncheckedIconToShow || checkedIconToShow) && (
          <>
            <span
              aria-hidden='true'
              className={cn(
                'absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in-out',
                checked ? 'opacity-0 duration-100 ease-out' : 'opacity-100',
              )}
            >
              {uncheckedIconToShow}
            </span>
            <span
              aria-hidden='true'
              className={cn(
                'absolute inset-0 flex size-full items-center justify-center transition-opacity duration-100 ease-out',
                checked ? 'opacity-100 duration-200 ease-in' : 'opacity-0',
              )}
            >
              {checkedIconToShow}
            </span>
          </>
        )}
      </span>
    </div>
  );
};

export default Toggle;
